import { Dialog } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import TimeActionsFormDialog from 'components/domain/time-entry/time-actions-form/TimeActionsFormDialog/TimeActionsFormDialog';
import TimeOffFormDialog from 'components/domain/time-off/form/TimeOffFormDialog/TimeOffFormDialog';
import CustomerSupportActionList from 'components/layout/navigation-drawer/NavHeader/CustomerSupportActionList/CustomerSupportActionList';
import { useOpennable } from 'hooks';
import useQuickSearchGraylog from 'hooks/analytics/useQuickSearchGraylog';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import useWhichOS from 'hooks/utils/useWhichOS';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useEffect } from 'react';
import { useFeatureFlags } from 'utils/features';
import CommandPallet from '../CommandPallet';

export interface ICommandPalletDialogProps {
  className?: string | object;
  isOpen?: boolean;
  onClose?: () => void;
}

const CommandPalletDialog = (props: ICommandPalletDialogProps) => {
  const { className, isOpen, onClose } = props;

  const commandPalletOpenable = useOpennable();
  const customerSupportOpenable = useOpennable();
  const isCommandPalletEnabled = useFeatureFlags('COMMAND_PALLET');
  const { isMacintosh } = useWhichOS();
  const quickSearchUserEvents = useQuickSearchGraylog();

  const timeEntryDialogDetails = useOpennable();
  const timeOffDialogDetails = useOpennable();

  useOnMount(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'k') {
        if ((isMacintosh() && e.ctrlKey) || (!isMacintosh() && e.altKey)) {
          if (isCommandPalletEnabled) {
            quickSearchUserEvents.searchOpened(true);
            commandPalletOpenable.open();
          }
        }
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  });

  useEffect(() => {
    if (!isNil(isOpen)) {
      if (isOpen) {
        commandPalletOpenable.open();
      } else {
        commandPalletOpenable.close();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function handleClose() {
    onClose?.();
    commandPalletOpenable.close();
  }

  function handleActionSubmit() {
    handleClose();
    window.location.reload();
  }

  const classes = classNames('command-pallet-dialog', className);

  return (
    <>
      <Dialog isOpen={commandPalletOpenable.isOpen} onClose={handleClose} className={classes}>
        {commandPalletOpenable.isOpen && (
          <CommandPallet
            onClose={handleClose}
            openSupport={customerSupportOpenable.open}
            onAddTimeEntry={timeEntryDialogDetails.open}
            onAddTimeOff={timeOffDialogDetails.open}
          />
        )}
      </Dialog>
      <HeaderDialog
        title={t('Support')}
        isOpen={customerSupportOpenable.isOpen}
        onClose={customerSupportOpenable.close}
      >
        <CustomerSupportActionList onSelect={customerSupportOpenable.close} className="mx-8 mb-6 pt-2" />
      </HeaderDialog>
      {timeEntryDialogDetails.isOpen && (
        <TimeActionsFormDialog
          type={'add'}
          isOpen={timeEntryDialogDetails.isOpen}
          onClose={timeEntryDialogDetails.close}
          onSubmit={handleActionSubmit}
          onDelete={handleActionSubmit}
        />
      )}

      {timeOffDialogDetails.isOpen && (
        <TimeOffFormDialog
          isOpen={timeOffDialogDetails.isOpen}
          onClose={timeOffDialogDetails.close}
          onSubmit={handleActionSubmit}
        />
      )}
    </>
  );
};

export default CommandPalletDialog;
